
import React, { FC, useEffect, useState, useCallback, startTransition } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import clientService from 'src/clientService';
import { SiteAdminDetail, DeploymentAdminDetail, LocationAdminDetail } from 'src/api/Api';
import SiteDetailMap from 'src/view/Admin/components/SiteDetailMap';
import DeploymentDialog from 'src/view/Admin/components/DeploymentDialog';
import AddLocationDialog from 'src/view/Admin/components/AddLocation/AddLocationDialog';
import { useAppState } from 'src/state-management/state-provider';
import LocationDetails from './LocationDetails';
import { formatDate } from 'src/util/date-utils';
import { Launch } from '@mui/icons-material';

const SiteDetails: FC = () => {
  const { projectId, siteId } = useParams<{ projectId: string; siteId: string }>();
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  const { selectedLocationId } = state.admin;
  const [site, setSite] = useState<SiteAdminDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deployments, setDeployments] = useState<DeploymentAdminDetail[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState<boolean>(false);
  const [openDeploymentDialog, setOpenDeploymentDialog] = useState<boolean>(false);
  const [openAddLocationDialog, setOpenAddLocationDialog] = useState<boolean>(false);

  const fetchSiteDetails = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const response = await clientService.api.getSiteDetailsAdminApiV1AdminSitesSiteIdGet(siteId);
      setSite(response.data);
    } catch (err) {
      setError('Failed to fetch site details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchSiteDetails();
  }, []);

  const handleLocationClick = useCallback(async (locationId: string) => {
    dispatch({ type: 'SET_ADMIN_SELECTED_LOCATION', payload: locationId });
    setDeploymentsLoading(true);
    try {
      const response = await clientService.api.getDeploymentsByLocationIdApiV1AdminLocationsLocationIdDeploymentsGet(locationId);
      setDeployments(response.data);
    } catch (err) {
      setError('Failed to fetch deployments.');
      console.error(err);
    } finally {
      setDeploymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocationId) {
      handleLocationClick(selectedLocationId);
    }
  }, [selectedLocationId]);

  const handleDeploymentCreated = useCallback((newDeployment: DeploymentAdminDetail) => {
    setDeployments((prevDeployments) => [newDeployment, ...prevDeployments]);
  }, []);

  const handleLocationAdded = useCallback((newLocation: LocationAdminDetail) => {
    setSite((prevSite) => {
      if (!prevSite) return null;
      return {
        ...prevSite,
        locations: [newLocation, ...(prevSite.locations || [])],
      };
    });
  }, []);

  const handleLocationDeleted = useCallback((deletedLocationId: string) => {
    setSite((prevSite) => {
      if (!prevSite) return null;
      const updatedLocations = prevSite.locations?.filter(
        (loc) => loc.location_id !== deletedLocationId
      );
      return {
        ...prevSite,
        locations: updatedLocations,
      };
    });
    dispatch({ type: 'SET_ADMIN_SELECTED_LOCATION', payload: undefined });
  }, []);

  const handleUploadClick = (deploymentId: string) => {
    startTransition(() => {
      dispatch({ type: 'SET_ADMIN_SELECTED_DEPLOYMENT', payload: deploymentId });
      navigate(`/admin/deployment/${deploymentId}/upload`);
    });
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!site) {
    return <Typography variant="h6">Site not found.</Typography>;
  }

  const selectedLocation = site?.locations?.find((l) => l.location_id === selectedLocationId);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" fontWeight="bold">
          {site.site_name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" fontWeight="bold" sx={{ mb: 2 }}>
                Site Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography color="text.secondary" variant="body2">Site Name</Typography>
                  <Typography variant="body1">{site.site_name}</Typography>
                </Grid>
                <Grid item xs={12} container alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">Total Locations</Typography>
                    <Typography variant="body1">{site.locations?.length || 0}</Typography>
                  </Box>
                  <Button variant="contained" onClick={() => setOpenAddLocationDialog(true)} endIcon={<AddLocationAltIcon />}>
                    Add Location
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" fontWeight="bold" sx={{ mb: 2 }}>
                Site Map
              </Typography>
              <Box sx={{ height: 300 }}>
                {site && (
                  <SiteDetailMap
                    site={site}
                    locations={site.locations || []}
                    onLocationClick={(locationId) => startTransition(() => dispatch({ type: 'SET_ADMIN_SELECTED_LOCATION', payload: locationId }))}
                    selectedLocationId={selectedLocationId}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {selectedLocation && (
          <Grid item xs={12} md={6}>
            <LocationDetails location={selectedLocation} deploymentCount={deployments.length} onLocationDeleted={handleLocationDeleted} />
          </Grid>
        )}
      </Grid>

      {selectedLocationId && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              Deployments
            </Typography>
            <Button variant="contained" onClick={() => setOpenDeploymentDialog(true)}>
              Add Deployment
            </Button>
          </Box>
          {deploymentsLoading ? (
            <CircularProgress />
          ) : deployments.length > 0 ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Deployment Name</TableCell>
                    <TableCell align='center'>Device Type</TableCell>
                    <TableCell align='center'>Device Name</TableCell>
                    <TableCell align='center'>Start Date</TableCell>
                    <TableCell align='center'>End Date</TableCell>
                    <TableCell align='center'>Total Files</TableCell>
                    <TableCell align='center'>Analyzed Files</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deployments.map((deployment) => (
                    <TableRow
                      key={deployment.deployment_id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align='center' component="th" scope="row">
                        {deployment.deployment_name}
                      </TableCell>
                      <TableCell align='center'>{deployment.device.type}</TableCell>
                      <TableCell align='center'>{deployment.device.human_readable_id}</TableCell>
                      <TableCell align='center'>{formatDate(deployment.start_date)}</TableCell>
                      <TableCell align='center'>{formatDate(deployment.end_date)}</TableCell>
                      <TableCell align='center'>{deployment.total_files > 0 ? <Chip label={deployment.total_files} size="small" color="primary" /> : null}</TableCell>
                      <TableCell align='center'>{deployment.total_inferences > 0 ? <Chip label={deployment.total_inferences} size="small" color="primary" /> : null}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            handleUploadClick(deployment.deployment_id);
                          }}
                        >
                          <Launch />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No deployments found for this location.</Typography>
          )}
        </Box>
      )}

      {selectedLocationId && (
        <DeploymentDialog 
          open={openDeploymentDialog} 
          onClose={() => setOpenDeploymentDialog(false)} 
          initialProjectId={projectId}
          initialSiteId={selectedLocationId} 
          onSuccess={(newDep: any) => handleDeploymentCreated(newDep as DeploymentAdminDetail)} 
        />
      )}
      {site && (
        <AddLocationDialog
          open={openAddLocationDialog}
          onClose={() => setOpenAddLocationDialog(false)}
          site={site}
          onLocationAdded={handleLocationAdded}
        />
      )}
    </Box>
  );
};

export default SiteDetails;

